import type { AsyncStore } from '@uppy/core';

/**
 * TTL must stay below the GIS access-token lifetime (~3600s) so a stale token is
 * never served. 50 min leaves a safe margin.
 */
const TOKEN_TTL_MS = 50 * 60 * 1000;

/**
 * Non-persistent, self-expiring token store for @uppy/google-drive-picker.
 *
 * Replaces the plugin's default localStorage store so that (a) a stale OAuth token
 * cannot survive into a later page load, and (b) an in-session token older than
 * TTL is treated as absent — forcing the plugin to re-authorize and obtain a fresh
 * token instead of feeding an expired one to the native Google Picker (which would
 * otherwise render its embedded "must sign in" screen and break the picker RPC).
 */
export function createInMemoryTokenStore(ttlMs: number = TOKEN_TTL_MS): AsyncStore {
  const map = new Map<string, { value: string; expires: number }>();
  return {
    getItem: async (key: string): Promise<string | null> => {
      const entry = map.get(key);
      if (!entry) {
        return null;
      }
      if (Date.now() > entry.expires) {
        map.delete(key);
        return null;
      }
      return entry.value;
    },
    setItem: async (key: string, value: string): Promise<void> => {
      map.set(key, { value, expires: Date.now() + ttlMs });
    },
    removeItem: async (key: string): Promise<void> => {
      map.delete(key);
    },
  };
}
